import { t as tGlobal } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCreateProduct } from "#modules/products/view/useCreateProduct";
import { Group } from "#shared/view/components/Group";
import { Modal } from "#shared/view/components/Modal";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { NativeSelectInputController } from "#shared/view/designSystem/inputs/NativeSelectInputController";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { TextInputController } from "#shared/view/designSystem/inputs/TextInputController";
import { Product, ProductTypeSchema, ProductTypeType } from "shared";

interface CreateProductFormData {
  name: string;
  price: number;
  fixedPrice?: number;
  type: ProductTypeType;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  onSuccess?: (product: Product) => void;
}

const productTypeOptions: { value: ProductTypeType; label: string }[] =
  ProductTypeSchema.options.map((option) => ({
    value: option,
    label: tGlobal(`qrl.productType.${option}`),
  }));

export const CreateProductModal = ({ opened, onClose, onSuccess }: Props) => {
  const { t } = useTranslation();
  const { createProduct, isPending } = useCreateProduct();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<CreateProductFormData>({
    defaultValues: {
      name: "",
      price: 0,
      fixedPrice: undefined,
      type: ProductTypeSchema.enum.CONSUMABLE,
    },
  });

  const handleCreateProduct = async (data: CreateProductFormData) => {
    const product = await createProduct({
      name: data.name,
      price: Math.round(data.price * 100),
      fixedPrice: data.fixedPrice ? Math.round(data.fixedPrice * 100) : undefined,
      type: data.type,
    });

    reset();
    onSuccess?.(product);
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t("products.createModal.title")} size="md">
      <form
        onSubmit={async (e) => {
          e.stopPropagation();
          await handleSubmit(handleCreateProduct)(e);
        }}
      >
        <Stack gap="md">
          <TextInputController
            name="name"
            label={t("quoteRequestLine.purchases.label")}
            control={control}
            placeholder={t("products.createModal.namePlaceholder")}
            required
          />

          <NumberInputController
            name="price"
            label={`${t("quoteRequestLine.purchases.cost")} (€)`}
            control={control}
            min={0}
            step={0.01}
            placeholder="0.00"
            required
          />

          <NumberInputController
            name="fixedPrice"
            label={`${t("quoteRequestLine.purchases.fixedCost")} (€)`}
            control={control}
            min={0}
            step={0.01}
            placeholder="0.00"
          />

          <NativeSelectInputController
            name="type"
            label={t("products.createModal.productType")}
            control={control}
            data={productTypeOptions}
            defaultValue={ProductTypeSchema.enum.CONSUMABLE}
            withEmptyData={false}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={handleClose} disabled={isPending}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={isPending} disabled={!isValid}>
              {t("products.createModal.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
